/* Pure aggregation for the Academics template. Kept out of the component so
   it can be exercised against real rows without rendering anything. */

// Subjects with only a handful of assessments produce averages that swing
// on a single mark. They are held out of the comparison and counted in the
// caption instead of sitting beside subjects with 300+ rows.
export const MIN_ASSESSMENTS = 20;

// The server sends missing cells as '', and Number('') is 0 rather than NaN,
// so an absent mark would otherwise chart as a genuine zero.
export const num = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// marks alone are not comparable: maxmarks ranges from 5 to 80 across tests.
export const scorePct = (row) => {
  const marks = num(row.marks);
  const max = num(row.maxmarks);
  if (marks === null || !max) return null;
  return (marks / max) * 100;
};

/* The source carries both "Annual Exam" and "ANNUAL EXAM" for one thing.
   Group on the folded form, then show whichever spelling appears most often
   — title-casing instead would turn the acronyms "PA 1" and "SEA / Project
   Work" into "Pa 1" and "Sea / Project Work". */
export function canonicalise(values) {
  const variantsByKey = new Map();
  values.forEach((raw) => {
    const text = String(raw ?? '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (!variantsByKey.has(key)) variantsByKey.set(key, new Map());
    const variants = variantsByKey.get(key);
    variants.set(text, (variants.get(text) || 0) + 1);
  });

  const display = new Map();
  variantsByKey.forEach((variants, key) => {
    let best = null;
    let bestCount = -1;
    variants.forEach((count, text) => {
      if (count > bestCount) {
        bestCount = count;
        best = text;
      }
    });
    display.set(key, best);
  });
  return display;
}

const labelFor = (display, value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Not stated';
  return display.get(text.toLowerCase()) || text;
};

const mean = (values) =>
  values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;

const round1 = (value) => (value === null ? null : Math.round(value * 10) / 10);

/** Everything the Academics charts need, derived in one pass over the rows. */
export function buildAcademics(rows) {
  const scored = rows
    .map((row) => ({ ...row, pct: scorePct(row) }))
    .filter((row) => row.pct !== null);

  if (scored.length === 0) return null;

  const subjectLabels = canonicalise(rows.map((row) => row.subject));
  const evaluationLabels = canonicalise(rows.map((row) => row.evaluation));

  // --- subject x term -------------------------------------------------
  const bySubject = new Map();
  scored.forEach((row) => {
    const subject = labelFor(subjectLabels, row.subject);
    if (!bySubject.has(subject)) bySubject.set(subject, { t1: [], t2: [], all: [] });
    const bucket = bySubject.get(subject);
    bucket.all.push(row.pct);
    const term = String(row.termname || '').trim().toLowerCase();
    if (term.endsWith('1')) bucket.t1.push(row.pct);
    else if (term.endsWith('2')) bucket.t2.push(row.pct);
  });

  const allSubjects = [...bySubject.entries()].map(([subject, b]) => ({
    subject,
    n: b.all.length,
    term1: round1(mean(b.t1)),
    term2: round1(mean(b.t2)),
    overall: round1(mean(b.all))
  }));

  const subjects = allSubjects
    .filter((s) => s.n >= MIN_ASSESSMENTS)
    .sort((a, b) => b.overall - a.overall);
  const thinSubjects = allSubjects.filter((s) => s.n < MIN_ASSESSMENTS);

  // --- movement between terms ----------------------------------------
  const movement = subjects
    .filter((s) => s.term1 !== null && s.term2 !== null)
    .map((s) => ({ subject: s.subject, change: round1(s.term2 - s.term1) }))
    .sort((a, b) => b.change - a.change);

  // --- assessment type -------------------------------------------------
  const byType = new Map();
  scored.forEach((row) => {
    const type = labelFor(evaluationLabels, row.evaluation);
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type).push(row.pct);
  });
  const assessmentTypes = [...byType.entries()]
    .map(([type, values]) => ({ type, n: values.length, avg: round1(mean(values)) }))
    .filter((t) => t.n >= 5)
    .sort((a, b) => b.avg - a.avg);

  // --- how students are spread ----------------------------------------
  const byStudent = new Map();
  scored.forEach((row) => {
    const key = row.enrollmentid ?? row.studentname;
    if (key === null || key === undefined) return;
    if (!byStudent.has(key)) byStudent.set(key, []);
    byStudent.get(key).push(row.pct);
  });

  const bands = [
    { band: 'Under 50%', from: 0, to: 50, students: 0 },
    { band: '50–59%', from: 50, to: 60, students: 0 },
    { band: '60–69%', from: 60, to: 70, students: 0 },
    { band: '70–79%', from: 70, to: 80, students: 0 },
    { band: '80–89%', from: 80, to: 90, students: 0 },
    { band: '90%+', from: 90, to: 101, students: 0 }
  ];
  byStudent.forEach((values) => {
    const avg = mean(values);
    const band = bands.find((b) => avg >= b.from && avg < b.to);
    if (band) band.students += 1;
  });

  const terms = [...new Set(scored.map((row) => String(row.termname || '').trim()).filter(Boolean))]
    .sort();

  // --- subject x assessment type, for the heat map ---------------------
  const cells = new Map();
  scored.forEach((row) => {
    const subject = labelFor(subjectLabels, row.subject);
    const type = labelFor(evaluationLabels, row.evaluation);
    const key = `${subject}|${type}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(row.pct);
  });
  const gridValues = new Map();
  cells.forEach((values, key) => gridValues.set(key, round1(mean(values))));

  // --- one line per subject across the terms, for the slope chart ------
  const slope = terms.map((term) => {
    const point = { term };
    subjects.forEach((s) => {
      point[s.subject] = term.endsWith('1') ? s.term1 : s.term2;
    });
    return point;
  });

  return {
    gridValues,
    slope,
    assessments: scored.length,
    students: byStudent.size,
    subjectCount: allSubjects.length,
    overallAvg: round1(mean(scored.map((row) => row.pct))),
    subjects,
    thinSubjects,
    movement,
    assessmentTypes,
    bands,
    terms: terms.sort()
  };
}

